import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  MessageSquare,
  Bug,
  Lightbulb,
  Loader2,
  Paperclip,
  X,
  Image as ImageIcon,
  Film,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService as api } from '../../lib/api';

interface FeedbackWidgetProps {
  systemContext?: 'THPT' | 'VSAT' | 'BOTH';
  /** 'floating' = fixed bottom corner button (default), 'inline' = text link for use in footers */
  variant?: 'floating' | 'inline';
}

export function FeedbackWidget({
  systemContext = 'THPT',
  variant = 'floating',
}: FeedbackWidgetProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<'BUG' | 'FEATURE_REQUEST' | 'OTHER'>('BUG');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      // Limit total files
      if (files.length + newFiles.length > 3) {
        toast.error('Bạn chỉ có thể tải lên tối đa 3 tệp.');
        return;
      }

      // Validate size
      for (const file of newFiles) {
        const isVideo = file.type.startsWith('video/');
        const limit = isVideo ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
        if (file.size > limit) {
          toast.error(`Tệp ${file.name} quá lớn. (Ảnh: 5MB, Video: 20MB)`);
          return;
        }
      }

      setFiles([...files, ...newFiles]);
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setLoading(true);
    try {
      // Upload files first
      const attachments: string[] = [];
      for (const file of files) {
        try {
          const res = await api.uploadFile(file);
          // Use the returned URL (relative or absolute depending on implementation)
          // If relative, ensure we store it correctly.
          attachments.push(res.url);
        } catch (err) {
          console.error('Upload failed', err);
          toast.error(`Không thể tải lên tệp: ${file.name}`);
          setLoading(false);
          return;
        }
      }

      await api.sendFeedback({
        content,
        type,
        systemContext,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        deviceInfo:
          typeof navigator !== 'undefined'
            ? {
                platform: navigator.platform,
                language: navigator.language,
                screen:
                  typeof window !== 'undefined'
                    ? { width: window.screen.width, height: window.screen.height }
                    : {},
              }
            : {},
        attachments,
      });
      toast.success('Cảm ơn bạn đã gửi ý kiến đóng góp!');
      setOpen(false);
      setContent('');
      setType('BUG');
      setFiles([]);
    } catch (error) {
      toast.error('Có lỗi xảy ra khi gửi góp ý. Vui lòng thử lại sau.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === 'inline' ? (
          <button className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors inline-flex items-center gap-1.5 cursor-pointer'>
            <MessageSquare className='h-3.5 w-3.5' />
            Góp ý
          </button>
        ) : (
          <Button
            variant='default'
            size='icon'
            className={`fixed z-[9999] h-12 w-12 rounded-full shadow-lg transition-all ${
              systemContext === 'THPT'
                ? 'bottom-4 left-4 md:bottom-8 md:left-8'
                : 'bottom-4 right-4 md:bottom-8 md:right-8'
            }`}
          >
            <MessageSquare className='h-6 w-6' />
            <span className='sr-only'>Góp ý</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Gửi ý kiến đóng góp</DialogTitle>
          <DialogDescription>
            Hãy giúp chúng tôi cải thiện ứng dụng bằng cách báo lỗi hoặc gửi ý tưởng mới.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='grid gap-2'>
            <Select value={type} onValueChange={(val: any) => setType(val)}>
              <SelectTrigger>
                <SelectValue placeholder='Chọn loại góp ý' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='BUG'>
                  <div className='flex items-center gap-2'>
                    <Bug className='h-4 w-4' />
                    <span>Báo lỗi</span>
                  </div>
                </SelectItem>
                <SelectItem value='FEATURE_REQUEST'>
                  <div className='flex items-center gap-2'>
                    <Lightbulb className='h-4 w-4' />
                    <span>Đề xuất tính năng</span>
                  </div>
                </SelectItem>
                <SelectItem value='OTHER'>
                  <div className='flex items-center gap-2'>
                    <MessageSquare className='h-4 w-4' />
                    <span>Khác</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='grid gap-2'>
            <Textarea
              placeholder='Mô tả chi tiết vấn đề hoặc ý tưởng của bạn...'
              value={content}
              onChange={e => setContent(e.target.value)}
              className='min-h-[100px]'
            />
          </div>

          {/* File Upload Section */}
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-medium text-muted-foreground'>Đính kèm (Tối đa 3)</span>
              <Button
                variant='outline'
                size='sm'
                className='h-8 gap-1'
                onClick={() => fileInputRef.current?.click()}
                disabled={files.length >= 3 || loading}
              >
                <Paperclip className='h-3.5 w-3.5' />
                <span>Thêm ảnh/video</span>
              </Button>
              <input
                type='file'
                ref={fileInputRef}
                className='hidden'
                accept='image/*,video/*'
                multiple
                onChange={handleFileSelect}
              />
            </div>

            {files.length > 0 && (
              <div className='grid gap-2'>
                {files.map((file, index) => (
                  <div
                    key={index}
                    className='flex items-center gap-2 rounded-md border p-2 text-sm w-full'
                  >
                    {/* Icon */}
                    {file.type.startsWith('video/') ? (
                      <Film className='h-4 w-4 flex-shrink-0 text-blue-500' />
                    ) : (
                      <ImageIcon className='h-4 w-4 flex-shrink-0 text-green-500' />
                    )}

                    {/* Filename - Takes remaining space */}
                    <div className='w-0 flex-1'>
                      <p className='truncate text-sm font-medium leading-none' title={file.name}>
                        {file.name}
                      </p>
                    </div>

                    {/* Size & Action */}
                    <div className='flex items-center gap-2 flex-shrink-0'>
                      <span className='text-xs text-muted-foreground whitespace-nowrap'>
                        ({(file.size / 1024 / 1024).toFixed(1)}MB)
                      </span>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-6 w-6'
                        onClick={() => removeFile(index)}
                        disabled={loading}
                      >
                        <X className='h-3.5 w-3.5' />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className='text-[10px] text-muted-foreground'>
              Hỗ trợ ảnh (JPG, PNG) tối đa 5MB và video (MP4) tối đa 20MB.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button type='submit' onClick={handleSubmit} disabled={loading || !content.trim()}>
            {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {loading ? 'Đang gửi...' : 'Gửi góp ý'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
